import { inject, Injectable, InjectionToken } from '@angular/core';
import { LoadingSpinnerService } from './loading-spinner.service';

export interface CalculationLoaderConfig {
  /** The loader only appears once a calculation has been running longer than this. */
  thresholdMs: number;
}

export const CALCULATION_LOADER_CONFIG = new InjectionToken<CalculationLoaderConfig>('CALCULATION_LOADER_CONFIG', {
  providedIn: 'root',
  factory: () => ({ thresholdMs: 200 })
});

export interface CalculationLoaderOptions {
  /** Overrides the configured threshold for this run only. */
  thresholdMs?: number;
}

export interface CalculationScope {
  /**
   * Runs a long synchronous block. A synchronous block cannot be interrupted by a timer, so the loader is
   * shown (and a frame is painted) *before* the block when it is already visible or when the previous run of
   * the same key took longer than the threshold. Fast blocks run immediately with no extra delay.
   */
  heavySync<T>(work: () => T): Promise<T>;
}

/**
 * Shows the shared spinner only for calculations that run longer than a threshold.
 *
 *  - Async parts: a timer shows the spinner once the threshold elapses; it is hidden as soon as the run settles.
 *  - Sync parts:  use `scope.heavySync`, which learns each key's duration and pre-shows the spinner for known-slow blocks.
 *
 * Every run owns its own timer and show/hide pair and the spinner service is reference counted, so concurrent
 * calculations never hide each other's loader. Timers are always cleared in `finally`, so nothing leaks.
 */
@Injectable({
  providedIn: 'root'
})
export class CalculationLoaderService {
  private readonly spinner = inject(LoadingSpinnerService);
  private readonly config = inject(CALCULATION_LOADER_CONFIG);

  /** Last synchronous duration per key; bounded by the number of distinct keys in the code base. */
  private readonly lastSyncDurationMs = new Map<string, number>();

  async run<T>(key: string, task: (scope: CalculationScope) => Promise<T>, options?: CalculationLoaderOptions): Promise<T> {
    const thresholdMs = options?.thresholdMs ?? this.config.thresholdMs;
    let shown = false;
    const show = (): void => {
      if (!shown) {
        shown = true;
        this.spinner.show();
      }
    };

    const scope: CalculationScope = {
      heavySync: async <R>(work: () => R): Promise<R> => {
        if (!shown && (this.lastSyncDurationMs.get(key) ?? 0) >= thresholdMs) {
          show();
        }
        if (shown) {
          await this.nextPaint();
        }
        const start = performance.now();
        try {
          return work();
        } finally {
          this.lastSyncDurationMs.set(key, performance.now() - start);
        }
      }
    };

    const timer = setTimeout(show, Math.max(0, thresholdMs));
    try {
      return await task(scope);
    } finally {
      clearTimeout(timer);
      if (shown) {
        shown = false;
        this.spinner.hide();
      }
    }
  }

  /** Resolves after the browser has had a chance to paint; falls back to a timeout because hidden tabs never fire rAF. */
  private nextPaint(): Promise<void> {
    return new Promise<void>(resolve => {
      let settled = false;
      let fallback: ReturnType<typeof setTimeout>;
      const finish = (): void => {
        if (!settled) {
          settled = true;
          clearTimeout(fallback);
          resolve();
        }
      };
      fallback = setTimeout(finish, 100);
      requestAnimationFrame(() => setTimeout(finish));
    });
  }
}
